
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as exposed from '../exposed'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {UpdateType} from '../core/UpdateType';
import {FieldModelUpdateType} from '../core/UpdateType';
import {PlatformInfo} from './PlatformInfo';
import {FieldModelPlatformInfo} from './PlatformInfo';

/**
 * PlatformInfoUpdateNotify struct
 */
class PlatformInfoUpdateNotify {
  /**
   * Initialize struct
   * @param {!UpdateType=} updateType
   * @param {PlatformInfo=} previous
   * @param {PlatformInfo=} current
   * @constructor
   */
  constructor (updateType = new UpdateType(), previous = null, current = null) {
    this.UpdateType = updateType
    this.Previous = previous
    this.Current = current
  }

  /**
   * Copy struct (shallow copy)
   * @this {!PlatformInfoUpdateNotify}
   * @param {!PlatformInfoUpdateNotify} other Other struct
   * @returns {!PlatformInfoUpdateNotify} This struct
   */
  copy (other) {
    if (other.UpdateType != null) {
      this.UpdateType = UpdateType.fromObject(other.UpdateType)
    } else {
      this.UpdateType = null
    }
    if (other.Previous != null) {
      this.Previous = PlatformInfo.fromObject(other.Previous)
    } else {
      this.Previous = null
    }
    if (other.Current != null) {
      this.Current = PlatformInfo.fromObject(other.Current)
    } else {
      this.Current = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!PlatformInfoUpdateNotify}
   * @returns {!PlatformInfoUpdateNotify} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new PlatformInfoUpdateNotifyModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new PlatformInfoUpdateNotifyModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!PlatformInfoUpdateNotify}
   * @param {!PlatformInfoUpdateNotify} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof PlatformInfoUpdateNotify)) {
      throw new TypeError('Instance of PlatformInfoUpdateNotify is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!PlatformInfoUpdateNotify}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      UpdateType: ((this.UpdateType != null) ? this.UpdateType : null),
      Previous: ((this.Previous != null) ? this.Previous : null),
      Current: ((this.Current != null) ? this.Current : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return PlatformInfoUpdateNotify.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!PlatformInfoUpdateNotify} other Object value
   * @returns {!PlatformInfoUpdateNotify} Created struct
   */
  static fromObject (other) {
    return new PlatformInfoUpdateNotify().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!PlatformInfoUpdateNotify}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return PlatformInfoUpdateNotify.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!PlatformInfoUpdateNotify}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 5066
  }
}

export { PlatformInfoUpdateNotify };

/**
 * Fast Binary Encoding PlatformInfoUpdateNotify field model
 */
class FieldModelPlatformInfoUpdateNotify extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.UpdateType = new FieldModelUpdateType(buffer, 4 + 4)
    this.Previous = new fbe.FieldModelOptional(new FieldModelPlatformInfo(buffer, this.UpdateType.fbeOffset + this.UpdateType.fbeSize), buffer, this.UpdateType.fbeOffset + this.UpdateType.fbeSize)
    this.Current = new fbe.FieldModelOptional(new FieldModelPlatformInfo(buffer, this.Previous.fbeOffset + this.Previous.fbeSize), buffer, this.Previous.fbeOffset + this.Previous.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelPlatformInfoUpdateNotify}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelPlatformInfoUpdateNotify}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.UpdateType.fbeSize + this.Previous.fbeSize + this.Current.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelPlatformInfoUpdateNotify}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.UpdateType.fbeExtra + this.Previous.fbeExtra + this.Current.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelPlatformInfoUpdateNotify}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelPlatformInfoUpdateNotify.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelPlatformInfoUpdateNotify}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 5066
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelPlatformInfoUpdateNotify}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelPlatformInfoUpdateNotify}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.UpdateType.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.UpdateType.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.UpdateType.fbeSize

    if ((fbeCurrentSize + this.Previous.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Previous.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Previous.fbeSize

    if ((fbeCurrentSize + this.Current.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Current.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Current.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelPlatformInfoUpdateNotify}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelPlatformInfoUpdateNotify}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelPlatformInfoUpdateNotify}
   * @param {!PlatformInfoUpdateNotify} fbeValue Default value, defaults is new PlatformInfoUpdateNotify()
   * @returns {!PlatformInfoUpdateNotify} PlatformInfoUpdateNotify value
   */
  get (fbeValue = new PlatformInfoUpdateNotify()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelPlatformInfoUpdateNotify}
   * @param {!PlatformInfoUpdateNotify} fbeValue PlatformInfoUpdateNotify value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.UpdateType.fbeSize) <= fbeStructSize) {
      fbeValue.UpdateType = this.UpdateType.get()
    } else {
      fbeValue.UpdateType = new UpdateType()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.UpdateType.fbeSize

    if ((fbeCurrentSize + this.Previous.fbeSize) <= fbeStructSize) {
      fbeValue.Previous = this.Previous.get()
    } else {
      fbeValue.Previous = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Previous.fbeSize

    if ((fbeCurrentSize + this.Current.fbeSize) <= fbeStructSize) {
      fbeValue.Current = this.Current.get()
    } else {
      fbeValue.Current = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Current.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelPlatformInfoUpdateNotify}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelPlatformInfoUpdateNotify}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelPlatformInfoUpdateNotify}
   * @param {!PlatformInfoUpdateNotify} fbeValue PlatformInfoUpdateNotify value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelPlatformInfoUpdateNotify}
   * @param {PlatformInfoUpdateNotify} fbeValue PlatformInfoUpdateNotify value
   */
  setFields (fbeValue) {
    this.UpdateType.set(fbeValue.UpdateType)
    this.Previous.set(fbeValue.Previous)
    this.Current.set(fbeValue.Current)
  }
}

export { FieldModelPlatformInfoUpdateNotify };

/**
 * Fast Binary Encoding PlatformInfoUpdateNotify model
 */
class PlatformInfoUpdateNotifyModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelPlatformInfoUpdateNotify(this.buffer, 4)
  }

  /**
   * Get the PlatformInfoUpdateNotify model
   * @this {!PlatformInfoUpdateNotifyModel}
   * @returns {!FieldModelPlatformInfoUpdateNotify} model PlatformInfoUpdateNotify model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!PlatformInfoUpdateNotifyModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!PlatformInfoUpdateNotifyModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return PlatformInfoUpdateNotifyModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!PlatformInfoUpdateNotifyModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelPlatformInfoUpdateNotify.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!PlatformInfoUpdateNotifyModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!PlatformInfoUpdateNotifyModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!PlatformInfoUpdateNotifyModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!PlatformInfoUpdateNotifyModel}
   * @param {!PlatformInfoUpdateNotify} value PlatformInfoUpdateNotify value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!PlatformInfoUpdateNotifyModel}
   * @param {!PlatformInfoUpdateNotify} value PlatformInfoUpdateNotify value, defaults is new PlatformInfoUpdateNotify()
   * @return {!object} Deserialized PlatformInfoUpdateNotify value and its size
   */
  deserialize (value = new PlatformInfoUpdateNotify()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new PlatformInfoUpdateNotify(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new PlatformInfoUpdateNotify(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!PlatformInfoUpdateNotifyModel}
   * @param {!number} prev Previous PlatformInfoUpdateNotify model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { PlatformInfoUpdateNotifyModel };
